<template>
    <div class="fd-member-container">
        <div class="fd-member" :class="{ 'dimmed': !m.active }">
			<template v-if="ex && parDir">
				<template v-for="(pcm, index) in value.expanded">
					<div class="fd-member-parent"></div>
					<mouse-member-party-part :data="value.expanded" :part="partForExpanded(index)" :level="level+1"></mouse-member-party-part>
				</template>
			</template>
            <div class="fd-member-icns">
                <object-link :src-id="p.id" :src-type="SrcType.PARTY">
                    <sym-icon :name="canView ? (p.icon ? p.icon : 'party') : 'forbidden'"></sym-icon>
                </object-link>
            </div>
            <div class="fd-member-party">
				<member-party-name :editable="!ro && level===0" :member="cm"><more-opener v-if="p.id && level===0" v-model="moreOpen"></more-opener></member-party-name>
                <show-down v-if="p.id && level===0" :show="moreOpen" class="fd-member-options fd-small">
                    <div class="fd-member-options-badges">
                        <label><input type="checkbox" v-model="m.fc" :disabled="ro"> {{ env.msg('person.forContact') }}</label>
                        <label><input type="checkbox" v-model="m.fb" :disabled="ro"> {{ env.msg('person.forBusiness') }}</label>
                        <label><input type="checkbox" v-model="m.ft" :disabled="ro"> {{
                            env.msg('person.forTechnical')
                            }}</label>
                        <label><input type="checkbox" v-model="m.fi" :disabled="ro"> {{ env.msg('person.forInvoice') }}</label>
                        <label><input type="checkbox" v-model="m.au" :disabled="ro"> {{ env.msg('person.authorized') }}</label>
                    </div>
                    <div class="fd-member-option-dlink">
                        <div class="fd-member-options-dates">{{ env.msg('membership.enterDate') }}:
                            <temporal-part :data="m" :part="datePart('enter')" :readonly="ro"
                                           :viewmode="vm"></temporal-part>
                            {{ env.msg('membership.leaveDate') }}
                            <temporal-part :data="m" :part="datePart('leave')" :readonly="ro"
                                           :viewmode="vm"></temporal-part>
                        </div>
                        <div>
                            <sym-icon v-if="!ro" name="unlink" class="fd-delete-icon" @click="deleteMember"></sym-icon>
                        </div>
                    </div>
                </show-down>
                <template v-if="ex && !parDir">
                    <div v-if="expanded" class="fd-member-expanded">
                        <icon-button name="minus-circle-14" class="fd-member-expander" @click="expanded = false"></icon-button>
                        <mouse-member-party-part v-for="(d, index) in value.expanded" :data="value.expanded" :part="partForExpanded(index)" :level="level+1"></mouse-member-party-part>
                    </div>
                    <div v-else class="fd-member-expanded">
                        <icon-button name="plus-circle-14" class="fd-member-expander" @click="expanded = true"></icon-button>
                        <span class="fd-member-expanded-simple"><object-link v-for="ecm in value.expanded"
							 :src-id="ecm.party.id" :src-type="SrcType.PARTY">{{ ecm.party.fileAs }}</object-link></span>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, defineProps, inject, nextTick, ref, provide} from "vue";
import useEditor, {type EditorProps, editorProps} from "../Editor";
import {injectEnv} from "../../Environment";
import SymIcon from "../../helpers/SymIcon.vue";
import ObjectLink from "../../helpers/ObjectLink.vue";
import TemporalPart from "../temporal/TemporalPart.vue";
import MoreOpener from "../../helpers/MoreOpener.vue";
import ShowDown from "../../helpers/ShowDown.vue";
import IconButton from "../../helpers/IconButton.vue";
import type {MemberParty} from "@t/fd/editor/MemberParty";
import type {ActiveMembership, CustomerMember, PartySimple} from "@t/customer/CustomerMember";
import {SrcType} from "@t/meta/SourceType";
import type {Temporal} from "@t/fd/editor/Temporal";
import MemberPartyName from "@fd/editors/member/MemberPartyName.vue";
import {contextKey, parentOrgKey} from "@fd/PartInjectKeys";
import {addToMap, addTruthyToMap, cloneMap, ContextKey, hasValue, injectContext} from "@t/fd/AppContext";
import {hasAccess, ObjectAccessFlags} from "@t/security/ObjectAccess.ts";

export interface MemberEditorProps extends EditorProps {
    level: number,
	arrayIndex?: number
}

const props = defineProps({
    ...editorProps,
    level: {
        type: Number,
        default: 0
    },
	arrayIndex: Number
});
const emit = defineEmits(["array-delete"]);

const {value, ro, vm, meta} = useEditor(props as MemberEditorProps);
const env = injectEnv();
const parentContext = injectContext();
provide(contextKey, computed(() => {
	// Modify context to include reference to the member
	const ctx = cloneMap(parentContext.value);
	const t = cm.value.party.type;

	if (t === "ORGANIZATION" || t === "FAMILY") addTruthyToMap(ctx, ContextKey.CUSTOMER, cm.value.party.id);
	else if (t === "FREELANCER") {
		if (hasValue(ctx, ContextKey.CUSTOMER)) addToMap(ctx, ContextKey.PERSON, cm.value.party.id);
		else addToMap(ctx, ContextKey.CUSTOMER, cm.value.party.id);
	}
	else if (t === "BRANCH") addToMap(ctx, ContextKey.BRANCH, cm.value.party.id);
	else if (t === "INDIVIDUAL" || t === "JOB" || t === "ROBOT" || t === "ANONYMOUS") addToMap(ctx, ContextKey.PERSON, cm.value.party.id);
	return ctx;
}));

// direction of display
const parDir = inject(parentOrgKey);
const moreOpen = meta<boolean>("_moreOpen", props.level === 0 && parDir.value && props.arrayIndex === 0);
const expanded = meta<boolean>("_expanded", false);

const cm = computed<CustomerMember>(() => value.value);
const p = computed<PartySimple>(() => cm.value.party);
const m = computed<ActiveMembership>(() => cm.value.membership);

const canView = computed<boolean>(() => {
	return hasAccess(cm.value, "view");
});

const e = computed<CustomerMember[]>(() => {
    if (Array.isArray(cm.value.expanded)) return cm.value.expanded;
    return [];
});

const ex = computed(() => Array.isArray(value.value.expanded) && value.value.expanded.length > 0);


function datePart(property: string) {
    return <Temporal>{
        type: "temporal",
        timePolicy: 0,
        property: property
    };
}

function deleteMember() {
    emit("array-delete");
}

function partForExpanded(index: number): MemberParty {
    return {
        type: "member-party",
        property: index.toString()
    }
}
</script>

<style>
.fd-member {
    display: grid;
	grid-template-columns: 26px 1fr;
    line-height: var(--icn-size);
    width: 100%;
	/* made problems with handle alignment
    margin-top: var(--form-v-padding);
    margin-bottom: var(--form-v-padding); */
    box-sizing: border-box;
}

.fd-member-parent {
	background-color: var(--fg); /* this is not mistake, color is masked */
	width: 26px;
	min-height: var(--input-height);
	mask-image: var(--parent-arrow);
	mask-size: 26px 160px;
	mask-repeat: no-repeat;
	-webkit-mask-image: var(--parent-arrow);
	-webkit-mask-size: 26px 160px;
	-webkit-mask-repeat: no-repeat;
	box-sizing: border-box;
}

.fd-member.top-separ {
    border-top: 1px solid var(--border-color);
    padding-top: var(--form-v-padding);
}

.fd-member.bottom-separ {
    border-bottom: 1px solid var(--border-color);
    padding-bottom: var(--form-v-padding);
}

.fd-member-icns {
    width: 26px;
    flex-shrink: 0;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.fd-member-party {
    max-width: 400px;
    flex-basis: 100%;
    box-sizing: border-box;
}


.fd-member-options {
    padding: var(--form-v-padding) 0;
}

.fd-member-options-badges {
    display: grid;
    grid-template-columns: auto auto;
	column-gap: 4px;
}

.fd-member-options-dates {
    margin-top: var(--form-v-padding);
    display: flex;
    flex-direction: row;
    align-items: baseline;
    column-gap: 4px;
}

.fd-member-option-dlink {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
}

.fd-delete-icon {
    color: var(--red);
    cursor: pointer;
    font-size: 14px;
}

.fd-member-expanded {
    position: relative;
}

.fd-member-expander {
    position: absolute;
    top: 0;
    left: -26px;
    width: 26px;
    opacity: 0.5;
}

.fd-member-expander:hover {
    opacity: 1;
}

.fd-member-expanded-simple {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 8px;
}
</style>
